// useWbLiveFeed — browser subscription to the WB live feed (Tricivo Agent
// Phase 3).
//
// Opens a Server-Sent Events stream against GET /api/v1/wb/live and surfaces
// the latest agent weight reading. The browser's native EventSource can't
// set an Authorization header, so the access token rides along as a
// `?token=` query param (the server promotes it to a Bearer header before
// the usual JWT verification — see wb/live/controller.ts).
//
// Reconnection (and the token refresh that must precede it — a stale token
// baked into the SSE URL would 401 forever) is delegated to the shared
// useSseWithTokenRefresh helper. This hook just owns the parsing and the
// latest-reading state.

import { useState } from 'react';

import {
  sseBaseUrl,
  useSseWithTokenRefresh,
} from '../useSseWithTokenRefresh';

export interface WbWeightUpdate {
  type: 'weight_update';
  /** Numeric agent id (raw, not enc_id — the agent surface speaks integers). */
  agentId: number;
  weighbridgeId: number | null;
  value: number;
  unit: string;
  stable: boolean;
  timestamp: number;
}

export interface WbCameraFrame {
  type: 'camera_frame';
  weighbridgeId: number | null;
  /** base64 JPEG (no data: prefix). */
  frame: string;
  timestamp: number;
}

export interface WbVehicleRecognizedEvent {
  type: 'vehicle_recognized';
  source: 'barcode' | 'anpr';
  registration: string;
  confidence: number;
  vehicle: {
    enc_id: string;
    registration: string;
    vehicle_type: string;
    haulier_name: string | null;
    stored_tare_kg: number | null;
    tare_status: string | null;
    max_capacity_kg: number | null;
    is_active: boolean;
  } | null;
}

export interface WbTicketSnapshotEvent {
  type: 'ticket_snapshot_saved';
  ticket_enc_id: string;
  line_enc_id: string | null;
  label: 'gross' | 'tare' | 'material' | 'other';
  image_url: string;
}

export interface WbTicketWeightUpdatedEvent {
  type: 'ticket_weight_updated';
  /** Rotating enc_id — never compare this across responses, coarse-invalidate instead. */
  ticketId: string;
  weightKg: number;
  // Server's WEIGHT_TYPE enum (types.ts) also has 'verify', but a verify
  // capture doesn't mutate any ticket field (service.ts) — it's a
  // read-only re-check — so there's nothing for a list-invalidation
  // consumer to react to. Deliberately excluded, not an oversight; the
  // parser drops 'verify' events rather than widening this type for them.
  weightType: 'gross' | 'tare';
  method: 'hardware' | 'manual' | 'stored';
}

export type WbLiveEvent =
  | WbWeightUpdate
  | WbCameraFrame
  | WbVehicleRecognizedEvent
  | WbTicketSnapshotEvent
  | WbTicketWeightUpdatedEvent
  | { type: 'ping' };

export interface UseWbLiveFeedOptions {
  onWeightUpdate?: (event: WbWeightUpdate) => void;
  /** Multi-Bridge UI — fired on each live camera-preview frame (per bridge). */
  onCameraFrame?: (event: WbCameraFrame) => void;
  /** Phase 2 — fired when the agent scans a vehicle QR sticker. */
  onVehicleRecognized?: (event: WbVehicleRecognizedEvent) => void;
  /** S67f — fired when the Agent claims a snapshot for a ticket line. */
  onTicketSnapshot?: (event: WbTicketSnapshotEvent) => void;
  /** S63G — fired whenever ANY client captures a ticket's weight (live-agent
   *  or manual). Consumers should treat this as a coarse invalidation signal
   *  only, never match `ticketId` against a cached enc_id. */
  onTicketWeightUpdated?: (event: WbTicketWeightUpdatedEvent) => void;
  enabled?: boolean;
}

export interface UseWbLiveFeedResult {
  connected: boolean;
  lastReading: WbWeightUpdate | null;
  lastFrame: WbCameraFrame | null;
}

function parseWeightUpdate(raw: unknown): WbWeightUpdate | null {
  if (typeof raw !== 'string') return null;
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (typeof data['value'] !== 'number' || typeof data['stable'] !== 'boolean') {
    return null;
  }
  return {
    type: 'weight_update',
    agentId: typeof data['agentId'] === 'number' ? data['agentId'] : 0,
    weighbridgeId:
      typeof data['weighbridgeId'] === 'number' ? data['weighbridgeId'] : null,
    value: data['value'],
    unit: typeof data['unit'] === 'string' ? data['unit'] : 'kg',
    stable: data['stable'],
    timestamp:
      typeof data['timestamp'] === 'number' ? data['timestamp'] : Date.now(),
  };
}

function parseCameraFrame(raw: unknown): WbCameraFrame | null {
  if (typeof raw !== 'string') return null;
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (typeof data['frame'] !== 'string' || data['frame'].length === 0) return null;
  return {
    type: 'camera_frame',
    weighbridgeId:
      typeof data['weighbridge_id'] === 'number' ? data['weighbridge_id'] : null,
    frame: data['frame'],
    timestamp:
      typeof data['timestamp'] === 'number' ? data['timestamp'] : Date.now(),
  };
}

function parseVehicleRecognized(raw: unknown): WbVehicleRecognizedEvent | null {
  if (typeof raw !== 'string') return null;
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (typeof data['registration'] !== 'string') return null;
  const source = data['source'];
  if (source !== 'barcode' && source !== 'anpr') return null;
  const rawVehicle = data['vehicle'];
  const vehicle =
    rawVehicle !== null && typeof rawVehicle === 'object'
      ? (rawVehicle as WbVehicleRecognizedEvent['vehicle'])
      : null;
  return {
    type: 'vehicle_recognized',
    source,
    registration: data['registration'],
    confidence: typeof data['confidence'] === 'number' ? data['confidence'] : 1.0,
    vehicle,
  };
}

function parseTicketSnapshot(raw: unknown): WbTicketSnapshotEvent | null {
  if (typeof raw !== 'string') return null;
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (typeof data['ticket_enc_id'] !== 'string') return null;
  const label = data['label'];
  if (label !== 'gross' && label !== 'tare' && label !== 'material' && label !== 'other') return null;
  if (typeof data['image_url'] !== 'string') return null;
  return {
    type: 'ticket_snapshot_saved',
    ticket_enc_id: data['ticket_enc_id'],
    line_enc_id: typeof data['line_enc_id'] === 'string' ? data['line_enc_id'] : null,
    label,
    image_url: data['image_url'],
  };
}

function parseTicketWeightUpdated(raw: unknown): WbTicketWeightUpdatedEvent | null {
  if (typeof raw !== 'string') return null;
  let data: Record<string, unknown>;
  try {
    data = JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (typeof data['ticketId'] !== 'string' || data['ticketId'].length === 0) return null;
  if (typeof data['weightKg'] !== 'number') return null;
  // 'verify' is a valid server weightType but is intentionally not
  // recognised here — see the comment on WbTicketWeightUpdatedEvent.
  const weightType = data['weightType'];
  if (weightType !== 'gross' && weightType !== 'tare') return null;
  const method = data['method'];
  if (method !== 'hardware' && method !== 'manual' && method !== 'stored') return null;
  return {
    type: 'ticket_weight_updated',
    ticketId: data['ticketId'],
    weightKg: data['weightKg'],
    weightType,
    method,
  };
}

export function useWbLiveFeed(
  options: UseWbLiveFeedOptions = {},
): UseWbLiveFeedResult {
  const {
    onWeightUpdate,
    onCameraFrame,
    onVehicleRecognized,
    onTicketSnapshot,
    onTicketWeightUpdated,
    enabled = true,
  } = options;
  const [lastReading, setLastReading] = useState<WbWeightUpdate | null>(null);
  const [lastFrame, setLastFrame] = useState<WbCameraFrame | null>(null);

  const { connected } = useSseWithTokenRefresh(
    (token) =>
      `${sseBaseUrl()}/wb/live${
        token ? `?token=${encodeURIComponent(token)}` : ''
      }`,
    {
      weight_update: (ev) => {
        const reading = parseWeightUpdate(ev.data);
        if (reading === null) return;
        setLastReading(reading);
        onWeightUpdate?.(reading);
      },
      camera_frame: (ev) => {
        const frame = parseCameraFrame(ev.data);
        if (frame === null) return;
        setLastFrame(frame);
        onCameraFrame?.(frame);
      },
      vehicle_recognized: (ev) => {
        const event = parseVehicleRecognized(ev.data);
        if (event === null) return;
        onVehicleRecognized?.(event);
      },
      ticket_snapshot_saved: (ev) => {
        const event = parseTicketSnapshot(ev.data);
        if (event === null) return;
        onTicketSnapshot?.(event);
      },
      ticket_weight_updated: (ev) => {
        const event = parseTicketWeightUpdated(ev.data);
        if (event === null) return;
        onTicketWeightUpdated?.(event);
      },
    },
    { enabled, realm: 'tenant' },
  );

  return { connected, lastReading, lastFrame };
}
